import { Product } from '@shared/components/ui/Product';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { faCheck, faLocationDot, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { BatchSummary } from '@shared/models/BatchSummary';
import { sanitizeBatchNumberForDisplay } from '@shared/utils/sanitizeBatchDisplay';
import { formatLineBatchDate } from '@shared/utils/formatLineBatchDate';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@shared/hooks/usePermissions';
import React from 'react';
import { LineBatchMeta } from './LineBatchMeta';

interface ProductToLocate {
  id: string;
  product_id?: number;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location?: string;
  image?: string;
  batch_id?: number | null;
  batch?: BatchSummary | null;
}

interface LocatingProductBlockProps {
  product: ProductToLocate;
  currentLocation: {
    id: number;
    code: string;
  };
  quantityToLocate: number;
  onQuantityChange: (quantity: number) => void;
  onConfirm: (batchId?: number) => void;
  onCancel?: () => void;
  isLocating?: boolean;
  batchSelectionCandidates?: ProductToLocate[] | null;
  selectedBatchLineId?: string | null;
  onBatchLineSelect?: (lineId: string) => void;
}

export const LocatingProductBlock: React.FC<LocatingProductBlockProps> = ({
  product,
  currentLocation,
  quantityToLocate,
  onQuantityChange,
  onConfirm,
  onCancel,
  isLocating = false,
  batchSelectionCandidates = null,
  selectedBatchLineId = null,
  onBatchLineSelect,
}) => {
  const { t } = useTranslation('locationordersDetailModule');
  const { hasPermission } = usePermissions();

  const requiresBatchSelection = (batchSelectionCandidates?.length ?? 0) > 1;
  const activeProduct =
    requiresBatchSelection && selectedBatchLineId
      ? (batchSelectionCandidates?.find((line) => line.id === selectedBatchLineId) ?? product)
      : product;

  const maxQuantity = activeProduct.quantity || activeProduct.totalQuantity;
  const hasLocatePermission = hasPermission('location-orders.locate-picking-return');
  const canLocate =
    hasLocatePermission &&
    quantityToLocate > 0 &&
    quantityToLocate <= maxQuantity &&
    (!requiresBatchSelection || !!selectedBatchLineId);

  const formatBatchOptionLabel = (line: ProductToLocate) => {
    const batchNumber = line.batch?.batch_number
      ? sanitizeBatchNumberForDisplay(line.batch.batch_number)
      : line.batch_id != null
        ? `#${line.batch_id}`
        : t('locate_modal.batch_unknown', { defaultValue: 'Sin lote' });
    const expiry = line.batch?.expiry_date ? formatLineBatchDate(line.batch.expiry_date) : '';
    const pending = t('locate_modal.batch_pending', {
      defaultValue: '{{qty}} pend.',
      qty: line.quantity,
    });
    return expiry ? `${batchNumber} — ${expiry} (${pending})` : `${batchNumber} (${pending})`;
  };

  const handleQuantityChange = (newQuantity: number) => {
    onQuantityChange(Math.max(1, Math.min(newQuantity, maxQuantity)));
  };

  return (
    <div className="bg-background border border-border rounded-lg p-4">
      {/* Header simple - igual que QuantityConfirm */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          {t('locate_modal.locating', { defaultValue: 'Confirmar ubicación' })}
        </h3>
        <div className="inline-flex items-center h-[40px] bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-3 rounded-full border border-blue-200 dark:border-blue-800">
          <FontAwesomeIcon icon={faLocationDot} className="w-3.5 h-3.5 mr-2" />
          <span className="font-medium text-sm whitespace-nowrap">{currentLocation.code}</span>
        </div>
      </div>

      {/* Producto usando componente Product */}
      <div className="mb-4">
        <Product
          id={product.product_id ?? 0}
          src={product.image || '/imagen/example.webp'}
          name={product.name}
          code={product.code}
          showImageModal={true}
          enableLineClamp={true}
        />
        {!requiresBatchSelection && <LineBatchMeta batch={product.batch} batchId={product.batch_id} className="mt-2" />}
      </div>

      {requiresBatchSelection && batchSelectionCandidates && onBatchLineSelect && (
        <div className="mb-4">
          <label className="block text-sm mb-2 text-foreground">
            {t('locate_modal.batch_label', { defaultValue: 'Lote' })}
          </label>
          <Select value={selectedBatchLineId ?? ''} onValueChange={onBatchLineSelect} disabled={isLocating}>
            <SelectTrigger data-testid="locating-batch-select">
              <SelectValue
                placeholder={t('locate_modal.batch_placeholder', {
                  defaultValue: 'Selecciona el lote a ubicar',
                })}
              />
            </SelectTrigger>
            <SelectContent>
              {batchSelectionCandidates.map((line) => (
                <SelectItem key={line.id} value={line.id}>
                  {formatBatchOptionLabel(line)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedBatchLineId && (
            <LineBatchMeta batch={activeProduct.batch} batchId={activeProduct.batch_id} className="mt-2" />
          )}
        </div>
      )}

      {/* Selector de cantidad compacto - igual que QuantityConfirm */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">
            {t('locate_modal.quantity_to_locate', { defaultValue: 'Cantidad a ubicar' })}
          </span>
          <span className="text-sm text-muted-foreground">
            {t('locate_modal.pending', { defaultValue: 'Pendiente' })}: {maxQuantity}
          </span>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <StepperInput
              value={quantityToLocate}
              onChange={handleQuantityChange}
              min={1}
              max={maxQuantity}
              step={1}
              allowNegative={false}
              disabled={requiresBatchSelection && !selectedBatchLineId}
              data-testid="locating-quantity-stepper"
            />
          </div>

          <button
            type="button"
            onClick={() => handleQuantityChange(maxQuantity)}
            disabled={quantityToLocate >= maxQuantity}
            className="px-2 py-1 text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="locating-quantity-max-button"
          >
            Máx
          </button>
        </div>
      </div>

      {/* Botones de acción compactos */}
      <div className="flex space-x-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLocating}
            className="flex-1 py-2 px-3 border border-border rounded font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            <FontAwesomeIcon icon={faTimes} className="w-4 h-4 mr-2" />
            {t('locate_modal.cancel', { defaultValue: 'Cancelar' })}
          </button>
        )}

        <button
          type="button"
          onClick={() => onConfirm(activeProduct.batch_id ?? undefined)}
          disabled={!canLocate || isLocating}
          className="flex-1 py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded font-medium focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {isLocating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('locate_modal.locating_loading', { defaultValue: 'Ubicando...' })}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheck} className="w-4 h-4 mr-2" />
              {t('locate_modal.confirm_button', { defaultValue: 'Ubicar' })}
            </>
          )}
        </button>
      </div>
    </div>
  );
};
